import { db } from "./firestore";
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  GeoPoint,
} from "firebase/firestore";

/**
 * Initialize or get user's advent progress
 */
export const getUserProgress = async (userId) => {
  try {
    const progressRef = doc(db, "adventProgress", userId);
    const docSnap = await getDoc(progressRef);

    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Initialize new user progress
      const initialProgress = {
        userId,
        totalScore: 0,
        lastUpdated: new Date().toISOString(),
      };
      await setDoc(progressRef, initialProgress);
      return initialProgress;
    }
  } catch (error) {
    console.error("Error getting user progress:", error);
    return null;
  }
};

/**
 * Update user's score for a specific day
 */
export const updateDayScore = async (userId, day, score, userName = null, guessCoords = null) => {
  try {
    const progressRef = doc(db, "adventProgress", userId);
    const docSnap = await getDoc(progressRef);

    const dayData = {
      score,
      completed: true,
      completedAt: new Date().toISOString(),
      guess: guessCoords ? new GeoPoint(guessCoords[0], guessCoords[1]) : null,
    };

    if (!docSnap.exists()) {
      // Initialize if doesn't exist
      await setDoc(progressRef, {
        userId,
        userName: userName || "Unknown Player",
        totalScore: score,
        [`day${day}`]: dayData,
        lastUpdated: new Date().toISOString(),
      });
    } else {
      const currentData = docSnap.data();
      const existingDayData = currentData[`day${day}`];

      // If day is already completed, don't allow updates
      if (existingDayData && existingDayData.completed) {
        console.log(`Day ${day} already completed. Score cannot be changed.`);
        return false;
      }

      const previousDayScore = existingDayData?.score || 0;
      const scoreDifference = score - previousDayScore;

      const updateData = {
        [`day${day}`]: dayData,
        totalScore: (currentData.totalScore || 0) + scoreDifference,
        lastUpdated: new Date().toISOString(),
      };

      // Add userName if it's missing
      if (!currentData.userName && userName) {
        updateData.userName = userName;
      }

      await updateDoc(progressRef, updateData);
    }

    return true;
  } catch (error) {
    console.error("Error updating day score:", error);
    return false;
  }
};

/**
 * Get leaderboard (top users by total score)
 */
export const getLeaderboard = async (limitCount = 10) => {
  try {
    const progressCollection = collection(db, "adventProgress");
    const q = query(
      progressCollection,
      orderBy("totalScore", "desc"),
      limit(100) // Get more to filter, then limit
    );
    const querySnapshot = await getDocs(q);

    const leaderboard = [];
    for (const doc of querySnapshot.docs) {
      const data = doc.data();

      // Check if user has completed at least one challenge
      const hasCompletedChallenge = Object.keys(data).some(
        key => key.startsWith('day') && data[key]?.completed
      );

      // Only include users who have completed at least one challenge
      if (hasCompletedChallenge) {
        const userName = data.userName || "Unknown Player";

        leaderboard.push({
          userId: doc.id,
          name: userName,
          totalScore: data.totalScore || 0,
          lastUpdated: data.lastUpdated,
        });
      }
    }

    // Return only the top limitCount after filtering
    return leaderboard.slice(0, limitCount);
  } catch (error) {
    console.error("Error getting leaderboard:", error);
    return [];
  }
};

/**
 * Initialize user with name in their progress document
 */
export const initializeUserProgress = async (userId, userName) => {
  try {
    const progressRef = doc(db, "adventProgress", userId);
    const docSnap = await getDoc(progressRef);

    if (!docSnap.exists()) {
      await setDoc(progressRef, {
        userId,
        userName,
        totalScore: 0,
        lastUpdated: new Date().toISOString(),
      });
    } else {
      // Update name if it doesn't exist
      const data = docSnap.data();
      if (!data.userName) {
        await updateDoc(progressRef, { userName });
      }
    }
    return true;
  } catch (error) {
    console.error("Error initializing user progress:", error);
    return false;
  }
};
